import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    ContactNotificationRow,
    PageResponse,
    fetchContactNotificationSummary,
    fetchContactNotificationsPage,
} from "../services/polarisService";

const PAGE_SIZE = 20;
const PLAN_COLORS = ["#2563eb", "#059669", "#d97706"];

function todayStr(): string {
    return new Date().toISOString().split("T")[0];
}

function daysAgoStr(n: number): string {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().split("T")[0];
}

function formatSentAt(iso: string): string {
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    } catch {
        return iso;
    }
}

function formTypeLabel(formType: string): string {
    if (formType === "DEMO_REQUEST") return "Contact (demo)";
    if (formType === "SALES_REQUEST") return "Sales contact";
    if (formType === "FAMILY_PLAN_REQUEST") return "Family Plan inquiry";
    return formType;
}

function getPageNumbers(current: number, total: number): (number | "ellipsis")[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i);
    const pages: (number | "ellipsis")[] = [];
    pages.push(0);
    if (current > 3) pages.push("ellipsis");
    for (let i = Math.max(1, current - 2); i <= Math.min(total - 2, current + 2); i++) {
        pages.push(i);
    }
    if (current < total - 4) pages.push("ellipsis");
    pages.push(total - 1);
    return pages;
}

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    totalElements: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    disabled?: boolean;
}

function Pagination({
    currentPage,
    totalPages,
    totalElements,
    pageSize,
    onPageChange,
    disabled = false,
}: PaginationProps) {
    if (totalPages <= 1) return null;
    const start = currentPage * pageSize + 1;
    const end = Math.min(currentPage * pageSize + pageSize, totalElements);
    const pages = getPageNumbers(currentPage, totalPages);

    return (
        <div className="pa-pagination">
            <div className="pa-pagination-info">
                Showing <strong>{start}–{end}</strong> of <strong>{totalElements}</strong> notifications
            </div>
            <div className="pa-pagination-controls">
                <button
                    type="button"
                    className="pa-page-btn"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={disabled || currentPage === 0}
                    aria-label="Previous page"
                >
                    Previous
                </button>
                {pages.map((p, i) =>
                    p === "ellipsis" ? (
                        <span key={`e-${i}`} className="pa-page-ellipsis">
                            …
                        </span>
                    ) : (
                        <button
                            type="button"
                            key={p}
                            className={`pa-page-btn${p === currentPage ? " active" : ""}`}
                            onClick={() => onPageChange(p as number)}
                            disabled={disabled}
                            aria-label={`Page ${(p as number) + 1}`}
                            aria-current={p === currentPage ? "page" : undefined}
                        >
                            {(p as number) + 1}
                        </button>
                    )
                )}
                <button
                    type="button"
                    className="pa-page-btn"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={disabled || currentPage >= totalPages - 1}
                    aria-label="Next page"
                >
                    Next
                </button>
            </div>
        </div>
    );
}

export default function PolarisSalesLeadsPanel() {
    const [from, setFrom] = useState(daysAgoStr(30));
    const [to, setTo] = useState(todayStr());
    const [summary, setSummary] = useState<{
        demoRequestCount: number;
        salesRequestCount: number;
        familyPlanRequestCount: number;
    } | null>(null);
    const [table, setTable] = useState<PageResponse<ContactNotificationRow> | null>(null);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [tablePaging, setTablePaging] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const hasDataForCurrentRangeRef = useRef(false);

    const load = useCallback(async () => {
        setError(null);
        const showFullLoading = !hasDataForCurrentRangeRef.current;
        if (showFullLoading) {
            setLoading(true);
        } else {
            setTablePaging(true);
        }
        try {
            const [sum, rows] = await Promise.all([
                fetchContactNotificationSummary(from, to),
                fetchContactNotificationsPage(from, to, page, PAGE_SIZE),
            ]);
            setSummary(sum);
            setTable(rows);
            hasDataForCurrentRangeRef.current = true;
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Failed to load sales data");
        } finally {
            setLoading(false);
            setTablePaging(false);
        }
    }, [from, to, page]);

    useEffect(() => {
        hasDataForCurrentRangeRef.current = false;
    }, [from, to]);

    useEffect(() => {
        void load();
    }, [load]);

    const rows = table?.content ?? [];

    return (
        <div className="pa-metrics-stack" data-testid="polaris-sales-leads-panel">
            <div className="pa-metric-card pa-metric-card-full">
                <div className="pa-metric-card-title">Contact form notifications</div>
                <p className="pa-muted" style={{ margin: "0 0 12px" }}>
                    Demo, sales contact, and Family Plan inquiry requests. Use Provision to convert a lead into a
                    custom organization offer.
                </p>

                <div className="pa-date-filter">
                    <label htmlFor="sales-from">From</label>
                    <input
                        id="sales-from"
                        type="date"
                        className="pa-date-input"
                        value={from}
                        max={to}
                        onChange={(e) => {
                            setFrom(e.target.value);
                            setPage(0);
                        }}
                    />
                    <label htmlFor="sales-to">To</label>
                    <input
                        id="sales-to"
                        type="date"
                        className="pa-date-input"
                        value={to}
                        min={from}
                        max={todayStr()}
                        onChange={(e) => {
                            setTo(e.target.value);
                            setPage(0);
                        }}
                    />
                </div>

                {error && <div className="pa-error">{error}</div>}
                {loading && <div className="pa-loading">Loading…</div>}

                {!loading && !error && summary && (
                    <div className="pa-type-row" style={{ marginBottom: 20 }}>
                        <div className="pa-type-card">
                            <div className="pa-type-count" style={{ color: PLAN_COLORS[0] }}>
                                {summary.demoRequestCount.toLocaleString()}
                            </div>
                            <div className="pa-type-label">Contact (demo)</div>
                        </div>
                        <div className="pa-type-card">
                            <div className="pa-type-count" style={{ color: PLAN_COLORS[1] }}>
                                {summary.salesRequestCount.toLocaleString()}
                            </div>
                            <div className="pa-type-label">Sales contact</div>
                        </div>
                        <div className="pa-type-card">
                            <div className="pa-type-count" style={{ color: PLAN_COLORS[2] }}>
                                {summary.familyPlanRequestCount.toLocaleString()}
                            </div>
                            <div className="pa-type-label">Family Plan inquiry</div>
                        </div>
                    </div>
                )}

                {!loading && !error && table && (
                    <>
                        <div
                            className="pa-table-wrapper"
                            style={{ opacity: tablePaging ? 0.55 : 1, transition: "opacity 0.15s" }}
                            aria-busy={tablePaging}
                        >
                            {tablePaging && (
                                <div className="pa-loading" style={{ padding: "8px 0", marginBottom: 8 }}>
                                    Loading page…
                                </div>
                            )}
                            <table className="pa-table">
                                <thead>
                                    <tr>
                                        <th>Sent</th>
                                        <th>Requester email</th>
                                        <th>Type</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} style={{ color: "var(--muted)", padding: "16px 12px" }}>
                                                No notifications in this date range.
                                            </td>
                                        </tr>
                                    ) : (
                                        rows.map((row, i) => (
                                            <tr key={`${row.sentAt}-${row.requesterEmail}-${i}`}>
                                                <td>{formatSentAt(row.sentAt)}</td>
                                                <td>{row.requesterEmail}</td>
                                                <td>{formTypeLabel(row.formType)}</td>
                                                <td>
                                                    <Link
                                                        className="pa-link-btn"
                                                        to={`/polaris-admin/sales/provision?email=${encodeURIComponent(row.requesterEmail)}${
                                                            row.id != null ? `&notificationId=${row.id}` : ""
                                                        }`}
                                                        data-testid="convert-lead-provision"
                                                    >
                                                        Provision
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {table.totalPages > 1 && (
                            <Pagination
                                currentPage={table.number}
                                totalPages={table.totalPages}
                                totalElements={table.totalElements}
                                pageSize={table.size}
                                onPageChange={setPage}
                                disabled={tablePaging}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
